import React, { useCallback } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CustomizeIcon from '@atlaskit/icon/core/migration/customize--preferences';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';

import type {
	ColorAndComponents,
	Iconography,
	SectionIds,
	Typography,
	VisualRefreshMode,
} from '../../types';
import ButtonGroup from '../button-group';

const styles = cssMap({
	link: {
		display: 'block',
		flexGrow: 1,
		textAlign: 'right',
	},
	label: {
		display: 'block',
		flexGrow: 1,
	},
	section: {
		paddingBlock: token('space.150'),
	},
	lastSection: {
		paddingBlockStart: token('space.150'),
	},
	withBorder: {
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width'),
		borderBlockEndColor: token('color.border'),
	},
});

type VisualRefreshProps = {
	colorAndComponents: ColorAndComponents;
	iconography: Iconography;
	onColorAndComponentsChange?(colorAndComponents: ColorAndComponents): void;
	onIconographyChange?(typography: Iconography): void;
	onTypographyChange?(typography: Typography): void;
	onVisualRefreshModeChange?(visualRefreshMode: VisualRefreshMode): void;
	typography: Typography;
	visualRefreshMode: VisualRefreshMode;
	testId?: string;
	hiddenSections?: SectionIds;
};

/**
 * __Visual refresh__
 *
 * The visual refresh section of the appearance controls form.
 */
const VisualRefresh = ({
	colorAndComponents,
	iconography,
	onColorAndComponentsChange,
	onIconographyChange,
	onTypographyChange,
	onVisualRefreshModeChange,
	typography,
	visualRefreshMode,
	testId,
	hiddenSections,
}: VisualRefreshProps) => {
	const toggleTypography = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.checked) {
				onTypographyChange?.('refreshed');
			} else {
				onTypographyChange?.('adg3');
			}
		},
		[onTypographyChange],
	);

	const toggleIconography = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.checked) {
				onIconographyChange?.('new');
			} else {
				onIconographyChange?.('legacy');
			}
		},
		[onIconographyChange],
	);

	const toggleNewDesignLanguage = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.checked) {
				onColorAndComponentsChange?.('new');
			} else {
				onColorAndComponentsChange?.('legacy');
			}
		},
		[onColorAndComponentsChange],
	);

	return (
		<>
			<Stack space="space.050">
				<Inline space="space.100">
					<Heading size="xsmall" as="h2" id="styles">
						Design language
					</Heading>
					<Box xcss={styles.link}>
						<Link href="/whats-new/atlassian-ui-refresh-updates" target="_blank">
							About our new design language
						</Link>
					</Box>
				</Inline>
			</Stack>
			<ButtonGroup
				options={[
					{ value: 'new', label: 'New' },
					{ value: 'legacy', label: 'Legacy' },
					{ value: 'custom', label: 'Custom', iconBefore: CustomizeIcon },
				]}
				selected={visualRefreshMode}
				onChange={onVisualRefreshModeChange}
				testId={testId ? `${testId}--visual-refresh-mode` : undefined}
				labelId="styles"
			/>
			{visualRefreshMode === 'custom' && (
				<Stack>
					{!hiddenSections?.includes('typography') && (
						<Stack
							space="space.150"
							xcss={cx(
								!hiddenSections?.includes('iconography') ||
									!hiddenSections?.includes('color-and-components')
									? styles.section
									: styles.lastSection,
								(!hiddenSections?.includes('iconography') ||
									!hiddenSections?.includes('color-and-components')) &&
									styles.withBorder,
							)}
						>
							<Inline space="space.025" alignBlock="center">
								<Box xcss={styles.label}>
									<Text size="small" id="typography">
										Typography <Lozenge appearance="new">Beta</Lozenge>
									</Text>
								</Box>
								<Toggle
									isChecked={typography !== 'adg3'}
									onChange={toggleTypography}
									testId={testId ? `${testId}--typography` : undefined}
								/>
							</Inline>
						</Stack>
					)}
					{!hiddenSections?.includes('iconography') && (
						<Inline
							space="space.025"
							alignBlock="center"
							xcss={cx(
								!hiddenSections?.includes('color-and-components')
									? styles.section
									: styles.lastSection,
								!hiddenSections?.includes('color-and-components') && styles.withBorder,
							)}
						>
							<Box xcss={styles.label}>
								<Text size="small" id="iconography">
									Iconography
								</Text>
							</Box>
							<Toggle
								isChecked={iconography === 'new'}
								onChange={toggleIconography}
								testId={testId ? `${testId}--iconography` : undefined}
							/>
						</Inline>
					)}
					{!hiddenSections?.includes('color-and-components') && (
						<Inline space="space.025" alignBlock="center" xcss={styles.lastSection}>
							<Box xcss={styles.label}>
								<Text size="small" id="iconography">
									New design language <Lozenge appearance="moved">Early access</Lozenge>
								</Text>
							</Box>
							<Toggle
								isChecked={colorAndComponents === 'new'}
								onChange={toggleNewDesignLanguage}
								testId={testId ? `${testId}--color-and-components` : undefined}
							/>
						</Inline>
					)}
				</Stack>
			)}
		</>
	);
};

export default VisualRefresh;
